import { getLogger } from '@foundation-player/logger';

/**
 * Check if a video element can play different media types
 *
 * @param video - the video element to test
 *
 * @see https://github.com/shaka-project/shaka-player/blob/a3d5dc54ff0e6eacd2af2602037cccabbd525c03/lib/media/media_source_engine.js#L208
 */
export function mediaProbe(video: HTMLVideoElement): Record<string, boolean> {
  const logger = getLogger('player.core.capability.mediaProbe');

  const mimeTypes = [
    // MP4 types
    'video/mp4; codecs="avc1.42E01E"',
    'video/mp4; codecs="avc3.42E01E"',
    'video/mp4; codecs="hev1.1.6.L93.90"',
    'video/mp4; codecs="hvc1.1.6.L93.90"',
    'video/mp4; codecs="hev1.2.4.L153.B0"; eotf="smpte2084"', // HDR HEVC
    'video/mp4; codecs="hvc1.2.4.L153.B0"; eotf="smpte2084"', // HDR HEVC
    'video/mp4; codecs="vp9"',
    'video/mp4; codecs="vp09.00.10.08"',
    'video/mp4; codecs="av01.0.01M.08"',
    'audio/mp4; codecs="mp4a.40.2"',
    'audio/mp4; codecs="ac-3"',
    'audio/mp4; codecs="ec-3"',
    'audio/mp4; codecs="opus"',
    'audio/mp4; codecs="flac"',
    'audio/mp4; codecs="dtsc"', // DTS Digital Surround
    'audio/mp4; codecs="dtse"', // DTS Express
    'audio/mp4; codecs="dtsx"', // DTS:X
    // WebM types
    'video/webm; codecs="vp8"',
    'video/webm; codecs="vp9"',
    'video/webm; codecs="vp09.00.10.08"',
    'audio/webm; codecs="vorbis"',
    'audio/webm; codecs="opus"',
    // MPEG2 TS types (video/ is also used for audio: https://bit.ly/TsMse)
    'video/mp2t; codecs="avc1.42E01E"',
    'video/mp2t; codecs="avc3.42E01E"',
    'video/mp2t; codecs="hvc1.1.6.L93.90"',
    'video/mp2t; codecs="mp4a.40.2"',
    'video/mp2t; codecs="ac-3"',
    'video/mp2t; codecs="ec-3"',
    // WebVTT types
    'text/vtt',
    'application/mp4; codecs="wvtt"',
    // TTML types
    'application/ttml+xml',
    'application/mp4; codecs="stpp"',
    // Containerless types
    'audio/aac',
    'audio/ac3',
    'audio/ec3',
    'audio/mpeg',
  ];

  const support: Record<string, boolean> = {};

  for (const mimeType of mimeTypes) {
    const result = video.canPlayType(mimeType);
    support[mimeType] = !!result;
    logger.debug(`Test native media type support for "${mimeType}"`, { mimeType, supported: result || false });
  }

  return support;
}
